// stores/cases.ts
import { defineStore } from 'pinia'
import orderBy from 'lodash/orderBy'

// Models
import { Case, CaseCategory } from '@/services/models/models'

// Services
import { CasesService } from '@/api'

type CaseId = Case['id']
type CategoryId = CaseCategory['id']

export interface CaseItem {
  id: string
  chance: number
  createAt: string
  exterior: string
  image: string
  name: string
  price: number
  rarity: string
  skin: string
  stattrack: boolean
  ticketEnd: number
  ticketStart: number
  type: string
  unstable: boolean
  updatedAt: string
  weapon: string
}

export interface CaseBestDrop {
  id?: string
  itemId?: string
  itemPrice?: number
  casePrice?: number
  createdAt?: string
  userId?: string
  userName?: string
  userAvatar?: string
  itemName?: string
  itemImage?: string
  rarity?: string
  weapon?: string
  skin?: string
  exterior?: string
  stattrak?: boolean
  chance?: number
}

interface CasesState {
  cases: Case[]
  vipCases: Case[]
  caseItems: CaseItem[] | null
  bestDrops: CaseBestDrop[]
  caseCategories: CaseCategory[]
  filters: {
    mode: 'all' | 'token'
    priceRange: [number, number] // dollars (UI) -> compare as cents
    isDiscounted: boolean
    searchTerm: string
  }
}

enum CaseStatus {
  DISABLED = 0,
  ACTIVE = 1,
  HIDDEN = 2,
}

export const useCasesStore = defineStore('cases', {
  state: (): CasesState => ({
    cases: [],
    vipCases: [],
    caseItems: null,
    bestDrops: [],
    caseCategories: [],
    filters: {
      mode: 'all',
      priceRange: [0, 1000],
      isDiscounted: false,
      searchTerm: '',
    },
  }),

  getters: {
    orderedCases(state): Case[] {
      return orderBy(state.cases, ['order', 'createdAt'], ['asc', 'asc'])
    },
    orderedVipCases(state): Case[] {
      return orderBy(state.vipCases, ['order', 'createdAt'], ['asc', 'asc'])
    },

    prepareFilteredCases(): Case[] {
      const base = (this.orderedCases as Case[]).filter(
        (caseItem: Case) => caseItem.status === CaseStatus.ACTIVE,
      )
      if (this.filters.mode === 'token') {
        return base.filter((it: Case) => it.tokenPrice > 0)
      }
      const [min, max] = this.filters.priceRange
      // assuming price stored in cents
      // assuming price stored in cents
      let result = base.filter((it: Case) => it.price >= min * 100 && it.price <= max * 100)

      if (this.filters.isDiscounted) {
        result = result.filter((it: Case) => (it.discountPercent ?? 0) > 0)
      }

      const searchQuery = this.filters.searchTerm?.trim().toLowerCase()
      if (searchQuery) {
        result = result.filter((it: Case) => it.name.toLowerCase().includes(searchQuery))
      }

      return result
    },

    notEmptyCaseCategories(): CaseCategory[] {
      return this.caseCategories.filter(
        (cat: CaseCategory) => this.getCasesByCategory(cat.id).length > 0,
      )
    },

    getCaseById: (state) => (id: CaseId) => state.cases.find((it: Case) => it.id === id),

    getCasesByIds: (state) => (ids: CaseId[]) =>
      state.cases.filter((box: Case) => ids.includes(box.id)),

    getCaseByName: (state) => (name: string) => state.cases.find((it: Case) => it.name === name),

    getCasesByCategory() {
      return (categoryId: CategoryId) =>
        (this.prepareFilteredCases as Case[]).filter((it: Case) => it.caseCategory === categoryId)
    },
  },

  actions: {
    // Api calls
    async fetchCasesIfNeeded() {
      if (this.cases.length === 0) {
        const cases = await CasesService.getV1Cases()
        this.setCases(cases)
      }
    },
    async fetchCaseItemsIfNeeded(caseId: string) {
      const response = await CasesService.getV1CasesItems(caseId)
      const items = Array.isArray(response) ? response : response.items
      const bestDrops = Array.isArray(response) ? [] : response.bestDrops ?? []
      this.setCaseItems(items)
      this.setBestDrops(bestDrops)
    },
    setCases(cases: Case[]) {
      this.cases = cases
    },
    setVipCases(vipCases: Case[]) {
      this.vipCases = vipCases
    },
    setCaseCategories(categories: CaseCategory[]) {
      this.caseCategories = categories
    },
    setFilters(filters: Partial<CasesState['filters']>) {
      this.filters = { ...this.filters, ...filters }
    },
    // Add or update a case // like after editing in admin panel
    setCase(updated: Case) {
      const i = this.cases.findIndex((it: Case) => it.id === updated.id)
      if (i > -1) this.cases[i] = updated
      else this.cases.push(updated)
    },
    setCaseItems(payload: CaseItem[]) {
      this.caseItems = payload
    },
    setBestDrops(payload: CaseBestDrop[]) {
      this.bestDrops = payload
    },
  },
})
